/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../core/viewmodel/ViewModel';
import { MaterialMenu } from '../../../../models/material/MaterialMenu';
import { SnackbarStore } from '../../../../stores/application/SnackbarStore';
import { AuthStore } from '../../../../stores/application/AuthStore';
import { CourseService } from '../../../../services/CourseService';
import { StoreProvider } from '../../../../stores/StoreProvider';
import * as H from 'history';
import { StudentManagementModel } from '../../../../models/components/StudentManagementModel';
import { useParams } from 'react-router-dom';
import { StudentTableRow } from '../../../../types/course/StudentTableRow';
import { Course } from '../../../../types/course/Course';
import { StudentService } from '../../../../services/StudentService';
import { EventStore } from '../../../../stores/application/EventStore';
import { Column } from '../../../../types/course/CourseColumn';
import { MenuItemType } from '../../../../types/course/CourseMenuItem';
import { CourseStore } from '../../../../stores/components/CourseStore';
import { MaterialCircularProgress } from '../../../../models/material/MaterialCircularProgress';
import { ChatStore } from '../../../../stores/components/ChatStore';

export class StudentManagementViewModel extends ViewModel {
	public menu: MaterialMenu = new MaterialMenu();
	private circularProgress = new MaterialCircularProgress();
	private routerParams: { courseId: string } = useParams();

	private readonly snackbarStore: SnackbarStore;
	private readonly authStore: AuthStore;
	private readonly eventStore: EventStore;
	private readonly courseStore: CourseStore;

	private readonly chatStore: ChatStore;

	private studentManagementModel: StudentManagementModel = new StudentManagementModel();
	private courseService: CourseService;
	private studentService: StudentService;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.snackbarStore = storeProvider.snackbarStore;
		this.authStore = storeProvider.authStore;
		this.eventStore = storeProvider.eventStore;
		this.courseStore = storeProvider.courseStore;
		this.courseService = storeProvider.courseService;
		this.studentService = storeProvider.studentService;
		this.chatStore = storeProvider.chatStore;
		this.queryCourse(this.routerParams.courseId);
		this.queryStudents(this.routerParams.courseId);
	}

	public isLoading(): boolean {
		return this.circularProgress.isLoading;
	}

	public getCourse(): Course {
		return this.courseStore.getCourse();
	}

	public setCourse(course: Course): void {
		this.courseStore.setCourse(course);
	}

	public getStudents(): StudentTableRow[] {
		return this.studentManagementModel.students;
	}

	public setStudents(students: StudentTableRow[]): void {
		this.studentManagementModel.setStudents(students);
	}

	public getHighlight(): boolean {
		return this.studentManagementModel.highlight;
	}

	public setHighlight(value: boolean): void {
		this.studentManagementModel.setHighlight(value);
	}

	public getDisableButton(): boolean {
		return this.studentManagementModel.disableInviteButton;
	}

	public columns: Column[] = [
		{ id: 'inviteKey', title: 'students.student_management.table.invite_key' },
		{ id: 'username', title: 'students.student_management.table.username' },
		{ id: 'progress', title: 'students.student_management.table.progress' },
		{ id: 'entries', title: 'students.student_management.table.entries' },
	];

	public menuItems: MenuItemType<StudentTableRow>[] = [
		{
			menuText: 'students.student_management.delete_account',
			menuFunction: (row: StudentTableRow | undefined): void => {
				if (row) {
					this.archiveStudent(this.getCourse().courseId, row.studentId);
					this.menu.closeMenu();
				}
			},
		},
	];

	public isNewAddedRow(rowLength: number, index: number): boolean {
		return rowLength - 1 === index;
	}

	public highlightNewlyAddedStudent(): ((rowLength: number, index: number) => boolean) | undefined {
		if (this.getHighlight()) {
			return this.isNewAddedRow;
		}
	}

	public queryCourse(courseId: string, highlightLastStudent = false): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();

		return this.courseService
			.getCourse(authToken, courseId)
			.then((course: Course) => {
				this.setCourse(course);
				if (highlightLastStudent) {
					this.setHighlight(true);
				}
			})
			.catch(() => {
				this.snackbarStore.showError('students.query_error');
			});
	}

	public createStudent(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		this.studentManagementModel.setDisableInviteButton(true);

		return this.studentService
			.createStudent(courseId, authToken)
			.then(() => {
				this.queryStudents(courseId, true);
				this.chatStore.reload = !this.chatStore.reload;
				this.snackbarStore.showSuccess('students.student_management.create_student');
				this.studentManagementModel.setDisableInviteButton(false);
			})
			.catch(() => {
				this.snackbarStore.showError('students.student_management.create_student_error');
				this.studentManagementModel.setDisableInviteButton(false);
			});
	}

	public archiveStudent(courseId: string, studentId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();

		return this.studentService
			.archiveStudent(studentId, authToken)
			.then(() => {
				this.queryStudents(courseId);
				this.chatStore.reload = !this.chatStore.reload;
				this.snackbarStore.showSuccess('students.student_management.archive_student');
			})
			.catch(() => {
				this.snackbarStore.showError('students.student_management.archive_student_error');
			});
	}

	public queryStudents(courseId: string, highlightLastStudent = false): Promise<void> | void {
		this.circularProgress.setIsLoading(true);
		const authToken: string = this.authStore.getAuthToken();

		return this.studentService
			.getStudents(courseId, authToken)
			.then((students: StudentTableRow[]) => {
				this.setStudents(students);
				if (highlightLastStudent) {
					this.setHighlight(true);
				}
				this.circularProgress.setIsLoading(false);
			})
			.catch(() => {
				this.snackbarStore.showError('students.query_error');
			});
	}
}
